import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

// Default site images
const DEFAULT_SITE_IMAGES = {
  home_uv_cocktail: { url: 'https://customer-assets.emergentagent.com/job_destilerija/artifacts/7lrzq7cu_IMG_9759.JPG', position: 50 },
  home_bartender: { url: 'https://customer-assets.emergentagent.com/job_destilerija/artifacts/p1z2fdnh_IMG_0149.JPG', position: 50 }
};

// Hero slider images - different for mobile (vertical) and desktop (horizontal)
const HERO_IMAGES_MOBILE = [
  {
    url: 'https://customer-assets.emergentagent.com/job_81cce0da-3404-4f70-8dc4-fe50cff96b25/artifacts/trc00pgi_IMG_9752.JPG',
    alt: 'ORIGIN Citrus koktel'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_81cce0da-3404-4f70-8dc4-fe50cff96b25/artifacts/ulgfi30p_IVA_3183.JPG',
    alt: 'ORIGIN koktel u ruci'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_81cce0da-3404-4f70-8dc4-fe50cff96b25/artifacts/2khjpjmj_IMG_3357.JPG',
    alt: 'ORIGIN boca u prirodi'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_81cce0da-3404-4f70-8dc4-fe50cff96b25/artifacts/6hxxlgnh_IVA_5382.JPG',
    alt: 'ORIGIN Citrus na stolu'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_81cce0da-3404-4f70-8dc4-fe50cff96b25/artifacts/r0ln38bu_IMG_3456.JPG',
    alt: 'ORIGIN craft gin'
  }
];

const HERO_IMAGES_DESKTOP = [
  {
    url: 'https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/vhl37o8o_5a-min.webp',
    alt: 'ОРИЏИН kolekcija - tri boce'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_destilerija/artifacts/t5pphhmr_Hero-img.webp',
    alt: 'ОРИЏИН boce u rukama'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_destilerija/artifacts/oaj0gzqa_IMG_7659.JPG',
    alt: 'Nazdravljanje koktelima'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_destilerija/artifacts/zy4x2bbw_IMG_7147.JPG',
    alt: 'ОРИЏИН na zalasku sunca'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_destilerija/artifacts/2h8u3ztp_IVI_9366.JPG',
    alt: 'ОРИЏИН Citrus u rukama'
  }
];

export default function Home() {
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [siteImages, setSiteImages] = useState(DEFAULT_SITE_IMAGES);

  // Fetch site images from API
  useEffect(() => {
    fetch('/api/site-images.php')
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) {
          setSiteImages({ ...DEFAULT_SITE_IMAGES, ...data });
        }
      })
      .catch(() => {});
  }, []);

  // Detect screen size for responsive images
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current image set based on screen size
  const heroImages = isMobile ? HERO_IMAGES_MOBILE : HERO_IMAGES_DESKTOP;

  // Static products as fallback
  const FALLBACK_PRODUCTS = [
    {
      id: "oridzin-classic",
      name_sr: "ORIGIN Classic",
      name_en: "ORIGIN Classic",
      description_sr: "Klasičan suvi džin sa bogatom aromom borovice",
      description_en: "Classic dry gin with rich juniper aroma",
      price: 4200,
      abv: 43,
      image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/9mrnwy6e_Dzin-Classic-new.webp"
    },
    {
      id: "oridzin-citrus",
      name_sr: "ORIGIN Citrus",
      name_en: "ORIGIN Citrus",
      description_sr: "Osvežavajući džin sa naglašenim citrus notama",
      description_en: "Refreshing gin with pronounced citrus notes",
      price: 4200,
      abv: 43,
      image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/2x2s1ysx_Dzin-Citrus-new.webp"
    },
    {
      id: "oridzin-uv",
      name_sr: "ORIGIN UV",
      name_en: "ORIGIN UV",
      description_sr: "Jedinstven džin koji menja boju - od plave do ljubičaste",
      description_en: "Unique color-changing gin - from blue to purple",
      price: 4600,
      abv: 43,
      image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/7nbi26rk_Dzin-UV-new.webp"
    }
  ];

  useEffect(() => {
    // Fetch products from PHP API, fallback to static data
    fetch('/api/products.php')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setProducts(data);
        } else {
          setProducts(FALLBACK_PRODUCTS);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching products:', err);
        setProducts(FALLBACK_PRODUCTS);
        setLoading(false);
      });
  }, []);

  // Auto-slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Reset slide when switching between mobile/desktop
  useEffect(() => {
    setCurrentSlide(0);
  }, [isMobile]);

  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sr-RS').format(price);
  };

  const getProductName = (product) => language === 'sr' ? product.name_sr : product.name_en;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-page">
      <SEO 
        title={language === 'sr' ? 'Početna' : 'Home'}
        description={language === 'sr' 
          ? 'Destilerija Kovačić - Premium ručno rađeni kraft džinovi iz Mokrina, Srbija. ОРИЏИН Classic, Citrus i UV džin. Naručite online.'
          : 'Destilerija Kovačić - Premium handcrafted gin from Mokrin, Serbia. ORIGIN Classic, Citrus and UV gin. Order online.'
        }
      />
      {/* Hero Section with Slider */}
      <section className="relative h-screen overflow-hidden">
        {/* Slider */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover object-center"
              />
              {/* Enhanced gradient overlay for better text visibility */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30 md:from-black/85 md:via-black/50 md:to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
              {/* Additional radial gradient around text area */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_20%_50%,rgba(0,0,0,0.8),transparent)]" />
            </div>
          ))}
        </div>

        {/* Slider Dots */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 transition-all ${
                index === currentSlide ? 'bg-gold w-8' : 'bg-white/30'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <div className="max-w-2xl">
              <div className="decorative-line mb-6 md:mb-8" />
              <p className="text-gold text-xs md:text-sm tracking-[0.3em] md:tracking-[0.4em] uppercase mb-4 md:mb-6 drop-shadow-lg">
                {t('hero_subtitle')}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white mb-2 md:mb-4 drop-shadow-2xl">
                Destilerija
              </h1>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl gold-gradient mb-6 md:mb-8 drop-shadow-2xl">
                Kovačić
              </h1>
              <p className="font-heading text-lg md:text-xl lg:text-2xl text-white/80 italic mb-3 md:mb-4 drop-shadow-lg">
                {t('hero_tagline')}
              </p>
              <p className="text-white/60 text-sm md:text-base lg:text-lg mb-8 md:mb-10 max-w-lg drop-shadow-md">
                {t('hero_description')}
              </p>
              <Link
                to="/shop"
                className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black px-8 md:px-10 py-3 md:py-4 text-xs md:text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300 shadow-lg shadow-gold/20"
                data-testid="hero-cta"
              >
                {t('hero_cta')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <button
          onClick={scrollToProducts}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 hover:text-gold transition-colors flex flex-col items-center gap-2 z-20"
          data-testid="scroll-indicator"
        >
          <span className="text-xs tracking-[0.2em] uppercase">Scroll</span>
          <ArrowDown size={20} className="animate-bounce" />
        </button>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 md:py-16 px-6" data-testid="products-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="decorative-line mx-auto mb-8" />
            <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
              {t('products_subtitle')}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white">
              {t('products_title')}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {products.map((product) => (
                <Link 
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group text-center"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="relative mb-4 overflow-hidden">
                    <div className="aspect-[3/4] flex items-center justify-center p-4">
                      <img
                        src={product.image_url}
                        alt={getProductName(product)}
                        className="max-h-full w-auto object-contain product-image-hover"
                      />
                    </div>
                  </div>
                  <h3 className="font-heading text-xl text-white group-hover:text-gold transition-colors mb-1">
                    {getProductName(product)}
                  </h3>
                  <p className="text-white/40 text-sm mb-2">
                    {product.abv}% vol.
                  </p>
                  <p className="text-gold text-lg font-medium">
                    {formatPrice(product.price)} <span className="text-sm">RSD</span>
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      addItem(product);
                    }}
                    className="mt-3 border border-gold/50 text-gold hover:bg-gold hover:text-black px-5 py-2 text-xs tracking-[0.1em] uppercase transition-all duration-300"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    {t('add_to_cart')}
                  </button>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-16 px-6 bg-surface">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="decorative-line mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
                {t('about_subtitle')}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-8">
                {language === 'sr' ? 'Kako je Destilerija Kovačić nastala?' : 'How was Destilerija Kovačić born?'}
              </h2>
              <p className="text-white/50 leading-relaxed mb-6">
                {t('about_story')}
              </p>
              <p className="text-white/50 leading-relaxed mb-10">
                {t('about_dedication')}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-gold hover:text-gold-light text-sm tracking-[0.1em] uppercase transition-colors"
                data-testid="story-read-more"
              >
                {language === 'sr' ? 'Pročitaj više o nama' : 'Read more about us'}
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_destilerija/artifacts/m7tzw57v_IVI_9279.JPG"
                  alt="Priprema koktela"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -inset-4 border border-gold/20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Product - UV Gin */}
      <section className="py-12 md:py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_destilerija/artifacts/7nbi26rk_Dzin-UV-new.webp"
                  alt="ОРИЏИН UV"
                  className="max-h-[500px] w-auto animate-float"
                />
                <div className="absolute inset-0 bg-purple-500/10 blur-[100px] -z-10" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="decorative-line mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
                {language === 'sr' ? 'Izdvojeni proizvod' : 'Featured product'}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                ОРИЏИН UV
              </h2>
              <p className="font-heading text-xl text-white/60 italic mb-6">
                {language === 'sr' ? 'Džin koji menja boju' : 'The color-changing gin'}
              </p>
              <p className="text-white/50 leading-relaxed mb-8">
                {language === 'sr' 
                  ? 'Naš najatraktivniji gin, prepoznatljiv po safirno plavoj boji koja se transformiše u magičnu ljubičastu nijansu pri kontaktu sa tonikom ili limunovim sokom. Vizuelno fascinantan, a istovremeno besprekoran u ukusu.'
                  : 'Our most attractive gin, recognizable by its sapphire blue color that transforms into a magical purple shade when mixed with tonic or lemon juice. Visually fascinating, while impeccable in taste.'}
              </p>
              <Link
                to="/product/oridzin-uv"
                className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300"
              >
                {language === 'sr' ? 'Upoznaj UV džin' : 'Discover UV gin'}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Rent Gin Bar Section */}
      <section className="py-12 md:py-16 px-6 bg-surface">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="decorative-line mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
                {language === 'sr' ? 'Posebna ponuda' : 'Special offer'}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                {language === 'sr' ? 'Rentiraj Džin Bar' : 'Rent a Gin Bar'}
              </h2>
              <p className="text-white/50 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Učinite vaš događaj nezaboravnim uz naš mobilni džin bar! Bilo da se radi o venčanju, korporativnom događaju, rođendanu ili privatnoj proslavi, naš tim donosi premium džin iskustvo direktno do vas.'
                  : 'Make your event unforgettable with our mobile gin bar! Whether it\'s a wedding, corporate event, birthday, or private celebration, our team brings the premium gin experience directly to you.'}
              </p>
              <p className="text-white/50 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Naš profesionalni barmeni pripremaju koktele sa našim ОРИЏИН džinovima, pružajući vašim gostima jedinstven vizuelni i ukusni spektakl. UV džin koji menja boju garantovano će oduševiti sve prisutne.'
                  : 'Our professional bartenders prepare cocktails with our ORIGIN gins, providing your guests with a unique visual and taste spectacle. The color-changing UV gin is guaranteed to delight everyone present.'}
              </p>
              <ul className="text-white/50 space-y-2 mb-8">
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-gold" />
                  {language === 'sr' ? 'Profesionalni barmeni' : 'Professional bartenders'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-gold" />
                  {language === 'sr' ? 'Premium ОРИЏИН džinovi' : 'Premium ORIGIN gins'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-gold" />
                  {language === 'sr' ? 'Elegantna oprema za bar' : 'Elegant bar equipment'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-gold" />
                  {language === 'sr' ? 'Prilagođeno vašim potrebama' : 'Customized to your needs'}
                </li>
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300"
              >
                {language === 'sr' ? 'Kontaktirajte nas' : 'Contact us'}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <div className="overflow-hidden">
                <img
                  src={siteImages.home_uv_cocktail?.url || DEFAULT_SITE_IMAGES.home_uv_cocktail.url}
                  alt="UV Gin Cocktail"
                  className="w-full h-auto object-cover max-h-[500px]"
                  style={{ objectPosition: `center ${siteImages.home_uv_cocktail?.position || 50}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="overflow-hidden">
                <img
                  src={siteImages.home_bartender?.url || DEFAULT_SITE_IMAGES.home_bartender.url}
                  alt="Priprema koktela"
                  className="w-full h-auto object-cover max-h-[450px]"
                  style={{ objectPosition: `center ${siteImages.home_bartender?.position || 50}%` }}
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="decorative-line mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
                {language === 'sr' ? 'Proces' : 'Process'}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                {language === 'sr' ? 'Tradicija i inovacija' : 'Tradition and innovation'}
              </h2>
              <p className="text-white/50 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Svaki naš džin prolazi kroz pažljivo kontrolisan proces destilacije u malim serijama. Verujemo da se kvalitet ne može ubrzati — zato svaku fazu proizvodnje nadgledamo lično, od odabira botaničkih sastojaka do finalne destilacije.'
                  : 'Each of our gins goes through a carefully controlled small-batch distillation process. We believe that quality cannot be rushed — that\'s why we personally oversee every stage of production, from botanical selection to final distillation.'}
              </p>
              <p className="text-white/50 leading-relaxed">
                {language === 'sr' 
                  ? 'Rezultat je džin sa dušom — autentičan, pun karaktera i stvoren za istinske ljubitelje dobrog pića.'
                  : 'The result is a gin with soul — authentic, full of character and created for true lovers of fine drinks.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-12 md:py-16 px-6 bg-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="decorative-line mx-auto mb-8" />
            <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
              {language === 'sr' ? 'Doživljaj' : 'Experience'}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              {language === 'sr' ? 'Više od pića' : 'More than a drink'}
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              {language === 'sr' 
                ? 'Svaka čaša našeg džina je putovanje kroz ukuse i arome. Od prvog mirisa kleke do završnih nota citrusa — svaki gutljaj priča svoju priču.'
                : 'Every glass of our gin is a journey through flavors and aromas. From the first scent of juniper to the finishing citrus notes — every sip tells its own story.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 border border-gold/30 flex items-center justify-center mx-auto mb-6">
                <span className="text-gold text-2xl font-heading">01</span>
              </div>
              <h3 className="font-heading text-xl text-white mb-4">
                {language === 'sr' ? 'Botanički sastojci' : 'Botanical Ingredients'}
              </h3>
              <p className="text-white/40 text-sm leading-relaxed">
                {language === 'sr' 
                  ? 'Pažljivo biramo preko 10 botaničkih sastojaka za svaki naš džin, osiguravajući jedinstven profil ukusa.'
                  : 'We carefully select over 10 botanical ingredients for each of our gins, ensuring a unique flavor profile.'}
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 border border-gold/30 flex items-center justify-center mx-auto mb-6">
                <span className="text-gold text-2xl font-heading">02</span>
              </div>
              <h3 className="font-heading text-xl text-white mb-4">
                {language === 'sr' ? 'Male serije' : 'Small Batches'}
              </h3>
              <p className="text-white/40 text-sm leading-relaxed">
                {language === 'sr' 
                  ? 'Proizvodnja u malim serijama omogućava nam potpunu kontrolu kvaliteta i konzistentnost svakog proizvoda.'
                  : 'Small batch production allows us complete quality control and consistency of every product.'}
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 border border-gold/30 flex items-center justify-center mx-auto mb-6">
                <span className="text-gold text-2xl font-heading">03</span>
              </div>
              <h3 className="font-heading text-xl text-white mb-4">
                {language === 'sr' ? 'Ručna izrada' : 'Handcrafted'}
              </h3>
              <p className="text-white/40 text-sm leading-relaxed">
                {language === 'sr' 
                  ? 'Svaka flaša je rezultat ručnog rada i posvećenosti braće Kovačić stvaranju savršenog džina.'
                  : 'Every bottle is the result of handwork and the Kovačić brothers\' dedication to creating the perfect gin.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brothers Section */}
      <section className="py-12 md:py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="decorative-line mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
                {language === 'sr' ? 'Iza brenda' : 'Behind the brand'}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                {language === 'sr' ? 'Braća Kovačić' : 'The Kovačić Brothers'}
              </h2>
              <p className="text-white/50 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Miloš i Ivan Kovačić su braća čija je strast ka džinu prerasla u uspešan porodični biznis. Od 2021. godine, iz Mokrina stvaraju džinove koji osvajaju ljubitelje širom Srbije.'
                  : 'Miloš and Ivan Kovačić are brothers whose passion for gin has grown into a successful family business. Since 2021, they have been creating gins from Mokrin that are winning over enthusiasts across Serbia.'}
              </p>
              <p className="text-white/50 leading-relaxed mb-8">
                {language === 'sr' 
                  ? 'Njihova filozofija je jednostavna: kvalitet ispred kvantiteta, autentičnost iznad trendova. Zato svaki naš džin ima dušu i priču.'
                  : 'Their philosophy is simple: quality over quantity, authenticity over trends. That\'s why every one of our gins has soul and story.'}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-gold hover:text-gold-light text-sm tracking-[0.1em] uppercase transition-colors"
              >
                {language === 'sr' ? 'Upoznaj nas' : 'Meet us'}
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/eerrwdl4_Braca-min-scaled.jpg"
                  alt="Braća Kovačić"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -inset-4 border border-gold/20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 px-6 bg-surface">
        <div className="max-w-3xl mx-auto text-center">
          <div className="decorative-line mx-auto mb-8" />
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-8">
            {language === 'sr'
              ? 'Nije nam namera samo da Vas naši proizvodi oduševe, već i da nam se ponovo vratite.'
              : "Our goal is not just to delight you with our products, but to have you come back again."}
          </h2>
          <Link
            to="/shop"
            className="inline-flex items-center gap-3 border border-gold text-gold hover:bg-gold hover:text-black px-10 py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
            data-testid="cta-shop"
          >
            {t('nav_shop')}
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
